// src/components/organization/dashboard/SubscriptionUsageChart.js
// Component for displaying subscription usage over time

import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useMySubscription from "../../../hooks/useMySubscription";
import LoadingSpinner from "../../common/LoadingSpinner";
import ErrorMessage from "../../common/ErrorMessage";

const SubscriptionUsageChart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { subscription, loading, error, refetch } = useMySubscription();

  // Check if we're on the upgrade page
  const isOnUpgradePage = location.pathname.includes('/upgrade') || location.pathname.includes('/packages');

  if (loading) {
    return (
      <div className="w-full">
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <LoadingSpinner message="Loading subscription details..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <ErrorMessage message={error} />
          <div className="text-center mt-4">
            <button 
              onClick={refetch}
              className="px-6 py-2 text-white rounded-lg transition-colors shadow-lg"
              style={{ backgroundColor: '#DE561F' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#C54E1C'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#DE561F'}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="w-full">
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 p-8 text-center">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-2xl font-semibold text-gray-900 mb-2">No Active Subscription</h3>
          <p className="text-gray-600 mb-6">You don't have an active subscription yet. Choose a plan to get started.</p>
          <button 
            className="px-6 py-3 text-white rounded-lg transition-colors shadow-lg font-semibold"
            style={{ backgroundColor: '#DE561F' }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#C54E1C'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#DE561F'}
            onClick={() => navigate('/organization/subscription/packages')}
          >
            View Packages
          </button>
        </div>
      </div>
    );
  }

  // Calculate dates and progress
  const startDate = subscription.startDate ? new Date(subscription.startDate) : null;
  const expireDate = subscription.endDate ? new Date(subscription.endDate) : null;
  const currentDate = new Date();
  
  // Check if dates are valid
  const isValidStartDate = startDate && !isNaN(startDate.getTime());
  const isValidExpireDate = expireDate && !isNaN(expireDate.getTime());
  
  const totalDays = (isValidStartDate && isValidExpireDate) 
    ? Math.ceil((expireDate - startDate) / (1000 * 60 * 60 * 24)) 
    : 0;
  const daysUsed = (isValidStartDate) 
    ? Math.max(0, Math.ceil((currentDate - startDate) / (1000 * 60 * 60 * 24))) 
    : 0;
  const daysRemaining = (isValidExpireDate) 
    ? Math.max(0, Math.ceil((expireDate - currentDate) / (1000 * 60 * 60 * 24))) 
    : 0;
  
  const usageProgress = totalDays > 0 ? Math.min((daysUsed / totalDays) * 100, 100) : 0;
  
  // Determine status configuration
  const getStatusConfig = () => {
    if (!subscription.isActive || daysRemaining <= 0) {
      return {
        bgGradient: "bg-gradient-to-br from-red-50 to-red-100",
        borderColor: "border-red-200",
        progressColor: "bg-red-500",
        statusColor: "bg-red-100 text-red-800",
        statusText: "Expired",
        urgencyColor: "text-red-600",
        iconColor: "text-red-600"
      };
    } else if (daysRemaining <= 30) {
      return {
        bgGradient: "bg-gradient-to-br from-yellow-50 to-orange-100",
        borderColor: "border-yellow-200",
        progressColor: "bg-yellow-500",
        statusColor: "bg-yellow-100 text-yellow-800",
        statusText: "Expiring Soon",
        urgencyColor: "text-yellow-600",
        iconColor: "text-yellow-600"
      };
    } else {
      return {
        bgGradient: "bg-gradient-to-br from-orange-50 to-orange-100",
        borderColor: "border-orange-200",
        progressColor: "bg-orange-600",
        statusColor: "bg-orange-100 text-orange-800",
        statusText: "Active",
        urgencyColor: "text-orange-600",
        iconColor: "text-orange-600"
      };
    }
  };

  const statusConfig = getStatusConfig();

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatShortDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getFeatureLabel = (key, value) => {
    const labels = {
      stuff: 'Staff Members',
      players: 'Players',
      teams: 'Teams',
      leagues: 'Leagues',
      events: 'Events',
      matches: 'Matches',
      competitions: 'Competitions'
    };
    
    const timeLabels = {
      stuff: '(Lifetime)',
      players: '(Lifetime)',
      teams: '(Lifetime)',
      leagues: '(Lifetime)',
      events: '(per month)',
      matches: '(per month)',
      competitions: '(per year)'
    };

    const displayValue = value === -1 ? 'Unlimited' : value.toLocaleString();
    return `${labels[key] || key} ${timeLabels[key] || ''}: ${displayValue}`;
  };

  return (
    <div className="w-full">
      {/* Main Subscription Card */}
      <div className={`${statusConfig.bgGradient} ${statusConfig.borderColor} border-2 rounded-xl p-6 mb-6 shadow-lg`}>
                {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <svg className={`w-8 h-8 mr-3 ${statusConfig.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#de561f' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{localStorage.getItem('userName') || 'Your Organization'}</h3>
                <p className="text-gray-600">Subscription Details</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${statusConfig.statusColor}`}>
              {statusConfig.statusText}
            </span>
            {!isOnUpgradePage && (
              <button 
                onClick={() => navigate('/organization/subscription/packages')}
                className="inline-flex items-center px-4 py-2 text-white rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                style={{ backgroundColor: '#de561f' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#c54e1c'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#de561f'}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                Upgrade Plan
              </button>
            )}
          </div>
        </div>

        {/* Package Info */}
        <div className="bg-white bg-opacity-70 rounded-lg p-5 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
                         <div className="flex items-center">
               <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#de561f' }}>
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
               </svg>
              <h4 className="font-bold text-xl text-gray-900">{subscription.packageName || 'Subscription Package'}</h4>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{formatCurrency(subscription.packagePrice || 0)}</div>
              <div className="text-sm text-gray-600">Annual Plan</div>
            </div>
          </div>
          
          {/* Features */}
          {subscription.packageFeatures && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(subscription.packageFeatures).map(([key, value]) => (
                                 <div key={key} className="flex items-center bg-orange-50 rounded-lg p-3">
                   <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#de561f' }}>
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                   </svg>
                   <span className="text-sm font-medium" style={{ color: '#de561f' }}>{getFeatureLabel(key, value)}</span>
                 </div>
              ))}
            </div>
          )}
        </div>

                 {/* Date Information */}
         <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
           <div className="bg-white bg-opacity-70 rounded-lg p-5 shadow-sm">
             <div className="flex items-center mb-3">
               <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#de561f' }}>
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
               </svg>
               <span className="font-semibold text-gray-700">Subscription Start</span>
             </div>
             {isValidStartDate ? (
               <>
                 <div className="text-xl font-bold text-gray-900">{formatShortDate(startDate)}</div>
                 <div className="text-sm text-gray-600">{formatDate(startDate)}</div>
               </>
             ) : (
               <div className="text-xl font-bold text-gray-500">Not Available</div>
             )}
           </div>
           
           <div className="bg-white bg-opacity-70 rounded-lg p-5 shadow-sm">
             <div className="flex items-center mb-3">
               <svg className={`w-5 h-5 mr-2 ${statusConfig.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
               </svg>
               <span className="font-semibold text-gray-700">Subscription Expires</span>
             </div>
             {isValidExpireDate ? (
               <>
                 <div className={`text-xl font-bold ${statusConfig.urgencyColor}`}>{formatShortDate(expireDate)}</div>
                 <div className="text-sm text-gray-600">{formatDate(expireDate)}</div>
               </>
             ) : (
               <div className="text-xl font-bold text-gray-500">Not Available</div>
             )}
           </div>
         </div>

        {/* Usage Progress */}
        <div className="bg-white bg-opacity-70 rounded-lg p-5 shadow-sm">
          <div className="mb-4">
            <div className="flex justify-between items-center mb-3">
              <span className="font-semibold text-gray-700">Subscription Timeline</span>
              <span className="font-bold text-lg text-gray-900">{Math.round(usageProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 shadow-inner">
              <div 
                className={`h-4 rounded-full transition-all duration-700 ${statusConfig.progressColor} shadow-sm`}
                style={{ width: `${usageProgress}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-600 mt-2">
              <span>Started</span>
              <span>Current</span>
              <span>Expires</span>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                 <div className="bg-white rounded-xl border border-gray-200 p-5 text-center shadow-sm hover:shadow-md transition-shadow">
           <div className="text-3xl font-bold mb-1" style={{ color: '#de561f' }}>{daysUsed}</div>
           <div className="text-sm text-gray-600">Days Used</div>
         </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-5 text-center shadow-sm hover:shadow-md transition-shadow">
          <div className={`text-3xl font-bold mb-1 ${statusConfig.urgencyColor}`}>
            {daysRemaining}
          </div>
          <div className="text-sm text-gray-600">Days Remaining</div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-5 text-center shadow-sm hover:shadow-md transition-shadow">
          <div className="text-3xl font-bold text-gray-900 mb-1">{totalDays}</div>
          <div className="text-sm text-gray-600">Total Days</div>
        </div>
        
                 <div className="bg-white rounded-xl border border-gray-200 p-5 text-center shadow-sm hover:shadow-md transition-shadow">
           <div className="text-3xl font-bold mb-1" style={{ color: '#de561f' }}>{formatCurrency(subscription.packagePrice || 0)}</div>
           <div className="text-sm text-gray-600">Annual Cost</div>
         </div>
      </div>

      {/* Renewal Alert */}
      {daysRemaining <= 30 && daysRemaining > 0 && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-yellow-600 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="flex-1">
              <h4 className="font-semibold text-yellow-800 mb-1">Subscription Renewal Reminder</h4>
              <p className="text-yellow-700 mb-3">Your subscription expires in {daysRemaining} days. Renew now to continue enjoying all features without interruption.</p>
                             <button 
                 className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium text-sm"
                 onClick={() => navigate('/organization/subscription/packages')}
               >
                 Renew Subscription
               </button>
            </div>
          </div>
        </div>
      )}

             {/* Expired Alert */}
       {(!subscription.isActive || daysRemaining <= 0) && (
        <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-red-600 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <h4 className="font-semibold text-red-800 mb-1">Subscription Expired</h4>
              <p className="text-red-700 mb-3">Your subscription has expired. Renew now to restore access to all features and continue managing your organization.</p>
                             <button 
                 className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
                 onClick={() => navigate('/organization/subscription/packages')}
               >
                 Renew Now
               </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionUsageChart; 